import { useRouter } from 'next/router'
import cn from 'classnames'
import { ArrowCta, type ArrowCtaProps } from '@mc/certificates-design-system'
import { Button, ButtonProps } from '@mc/design-system'
import { useTranslation } from '@mc/i18n'
import { paths } from '@mc/paths'
import Link from 'components/Link'
import {
  useCertificateSlug,
  useIsEnterpriseMember,
} from 'pages/certificates/common'
import { useCheckoutQuizEntry } from 'pages/certificates/experiments/mccerts-1485_v1-catalog-personalization-quiz/hooks/useCheckoutQuizEntry'
import { isPaintedDoorSlug } from 'pages/certificates/experiments/mccerts-2138-painted-door-v2'
import { TrackClick } from 'pages/certificates/tracking'

interface SignUpButtonProps extends Omit<ButtonProps, 'href'> {
  onClick?: (e: React.MouseEvent) => void
  arrow?: boolean
  arrowTheme?: ArrowCtaProps['arrowTheme']
  text?: string
  href?: string
  action?: string
  // Set false when a wrapping TrackClick sends its own event instead of purchaseClick.
  trackPurchaseClick?: boolean
  // Extra classes for the label span (e.g. a typography override).
  textClassName?: string
}

export const useAppendHrefFbParam = (href: string) => {
  const { query: { fbclid } = {} } = useRouter()

  if (href && fbclid) {
    const [baseUrl, searchParams] = href.split('?')

    const params = new URLSearchParams(searchParams)
    params.append('fbclid', fbclid as string)

    return `${baseUrl}?${params.toString()}`
  }

  return href
}

// TODO(MCCERTS-1675): Remove href override once single certs use the new asset checkout path
export const useSignup = ({
  onClick,
  href: hrefOverride,
}: {
  onClick?: (e: React.MouseEvent) => void
  href?: string
}) => {
  const { certSlug } = useCertificateSlug()
  const { quizHref, trackQuizExposure } = useCheckoutQuizEntry(certSlug, {
    enabled: !hrefOverride,
  })

  const href = hrefOverride ?? quizHref ?? paths.certificatesCheckout(certSlug)
  const product = isPaintedDoorSlug(certSlug)
    ? ('Certificate Topics' as const)
    : ('certificates' as const)

  const handleClick = (e: React.MouseEvent<HTMLElement>) => {
    trackQuizExposure()
    onClick?.(e)
  }

  const fullHref = useAppendHrefFbParam(href)

  return { fullHref, handleClick, trackQuizExposure, product }
}

export const SignUpButton = ({
  children,
  onClick,
  arrow,
  arrowTheme,
  text,
  href,
  action,
  trackPurchaseClick = true,
  textClassName,
  ...props
}: SignUpButtonProps) => {
  const { t } = useTranslation('@mc/certificates')

  const defaultText = t('marketingPages.common.signUp')
  const displayText = text ?? defaultText
  const { handleClick, fullHref, product } = useSignup({ onClick, href })
  const isEnterprise = useIsEnterpriseMember()

  if (isEnterprise) {
    return null
  }

  const buttonProps = {
    render: <Link href={fullHref} />,
    onClick: handleClick,
    ...props,
  }
  const label = (
    <span className={cn('flex items-center justify-center', textClassName)}>
      {displayText}
    </span>
  )

  return (
    <TrackClick
      event='purchaseClick'
      action={action ?? displayText}
      product={product}
      enabled={trackPurchaseClick}
    >
      {arrow ? (
        <ArrowCta {...buttonProps} arrowTheme={arrowTheme}>
          {label}
        </ArrowCta>
      ) : (
        <Button {...buttonProps}>{label}</Button>
      )}
    </TrackClick>
  )
}
